import throttle from 'lodash/throttle'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { FixedSizeList, ListOnScrollProps } from 'react-window'

type RenderProps = {
  ref: React.RefCallback<FixedSizeList>
  outerRef: React.RefObject<HTMLDivElement | null>
  style: React.CSSProperties
  height: number
  onScroll: (props: ListOnScrollProps) => void
}

type Props = {
  children: (renderProps: RenderProps) => React.ReactNode
  throttleTime?: number
  // Extra ref to also receive the list instance (e.g. InfiniteLoader's ref).
  // Merging happens inside a single stable callback so the list ref is never
  // transiently nulled by a new inline callback on each render.
  listRef?: React.Ref<FixedSizeList>
}

const setRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ;(ref as React.MutableRefObject<T | null>).current = value
  }
}

/**
 * Drives a react-window list from the window's scroll position so the list
 * grows to its full height and scrolls with the page instead of owning an
 * inner scrollbar.
 *
 * SLAM-1970: react-window has no built-in WindowScroller (the react-virtualized
 * component this replaces), so we bridge window scroll into the list here.
 */
const ReactWindowScroller = ({
  children,
  throttleTime = 10,
  listRef,
}: Props): React.ReactNode => {
  const ref = useRef<FixedSizeList | null>(null)
  const outerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(() => window.innerHeight)

  const setListRef = useCallback<React.RefCallback<FixedSizeList>>(
    node => {
      ref.current = node
      setRef(listRef, node)
    },
    [listRef],
  )

  // Document-relative top of the list. offsetTop would be relative to the
  // nearest positioned ancestor (the contacts form is positioned), which would
  // map window scroll to the wrong rows; getBoundingClientRect fixes that.
  const getListTop = useCallback(() => {
    const el = outerRef.current
    return el ? el.getBoundingClientRect().top + window.pageYOffset : 0
  }, [])

  // react-virtualized's WindowScroller tracked viewport resize internally.
  // react-window virtualizes against a fixed height, so keep it in sync with
  // the viewport or a grown window leaves a blank strip below the last row.
  useEffect(() => {
    const onResize = () => setHeight(window.innerHeight)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const syncListToWindow = () => {
      ref.current?.scrollTo(Math.max(0, window.pageYOffset - getListTop()))
    }
    // Sync once on mount so a restored/non-zero scroll position renders the
    // correct initial range instead of the top of the list.
    syncListToWindow()

    const handleWindowScroll = throttle(syncListToWindow, throttleTime)
    window.addEventListener('scroll', handleWindowScroll)
    return () => {
      handleWindowScroll.cancel()
      window.removeEventListener('scroll', handleWindowScroll)
    }
  }, [throttleTime, getListTop])

  const onScroll = useCallback(
    ({ scrollOffset, scrollUpdateWasRequested }: ListOnScrollProps) => {
      if (!scrollUpdateWasRequested) return
      const top = window.pageYOffset
      const adjustedScrollOffset = scrollOffset + Math.min(top, getListTop())
      // Tolerate sub-pixel getBoundingClientRect values so we don't fight the
      // browser with repeated ~1px-off scrollTo calls (visible jitter).
      if (Math.abs(adjustedScrollOffset - top) > 1) {
        window.scrollTo(0, adjustedScrollOffset)
      }
    },
    [getListTop],
  )

  return children({
    ref: setListRef,
    outerRef,
    height,
    // Height is driven by the window scroll above; letting the outer element
    // fill its content keeps the page (not the list) as the scroll container.
    style: { width: '100%', height: '100%', display: 'inline-block' },
    onScroll,
  })
}

export default ReactWindowScroller
